import gql from 'graphql-tag';

import { Query, Mutation } from 'core';
import { Subscription } from 'core/subscription';

const STORY_NOTIFICATION_FRAGMENT = gql`
  fragment story on Story {
    id
    title
    bodySnippet
    readLength
    space {
      id
      spaceType
      content {
        id
        slug
      }
    }
  }
`;

const PROJECT_NOTIFICATION_FRAGMENT = gql`
  fragment project on Project {
    id
    name
    slug
    snippet
    space {
      id
      spaceType
      content {
        id
        slug
      }
    }
    avatar {
      id
      url
      path
    }
  }
`;

const CONTRIBUTION_NOTIFICATION_FRAGMENT = gql`
  ${PROJECT_NOTIFICATION_FRAGMENT}
  fragment contribution on Contribution {
    id
    title
    description
    version
    project {
      ...project
    }
  }
`;

const ISSUE_NOTIFICATION_FRAGMENT = gql`
  ${PROJECT_NOTIFICATION_FRAGMENT}
  fragment issue on Issue {
    id
    title
    description
    project {
      ...project
    }
  }
`;

const THREAD_NOTIFICATION_FRAGMENT = gql`
  fragment thread on Thread {
    id
    title
    msg
    urlId
    snippet
    category {
      id
      urlId
    }
    forum {
      id
      name
      parentContent {
        id
        slug
      }
    }
  }
`;

const INITIATIVE_NOTIFICATION_FRAGMENT = gql`
  fragment initiative on Initiative {
    id
    title
    description
    avatar {
      id
      url
      path
    }
  }
`;

const PROFILE_NOTIFICATION_FRAGMENT = gql`
  fragment profile on Profile {
    id
    username
    fullName
    imageFallbackChar
    avatar {
      id
      path
      url
    }
  }
`;

const COMMENT_NOTIFICATION_FRAGMENT = gql`
  fragment comment on Comment {
    id
    snippet
  }
`;

const CONTENT_NOTIFICATION_FRAGMENT = gql`
  ${STORY_NOTIFICATION_FRAGMENT}
  ${PROJECT_NOTIFICATION_FRAGMENT}
  ${CONTRIBUTION_NOTIFICATION_FRAGMENT}
  ${ISSUE_NOTIFICATION_FRAGMENT}
  ${INITIATIVE_NOTIFICATION_FRAGMENT}
  ${THREAD_NOTIFICATION_FRAGMENT}
  ${PROFILE_NOTIFICATION_FRAGMENT}
  ${COMMENT_NOTIFICATION_FRAGMENT}
  fragment contentNotification on ContentInterface {
    id
    slug
    parentSlug
    isPrivate
    whitelabel
    __typename
    ... on Story {
      ...story
    }
    ... on Project {
      ...project
    }
    ... on Contribution {
      ...contribution
    }
    ... on Issue {
      ...issue
    }
    ... on Initiative {
      ...initiative
    }
    ... on Thread {
      ...thread
    }
    ... on Profile {
      ...profile
    }
  }
`;

const NOTIFICATION_TARGET_FRAGMENT = gql`
  ${CONTENT_NOTIFICATION_FRAGMENT}
  fragment target on ContentInterface {
    ...contentNotification
    ... on Comment {
      ...comment
      origin {
        ...contentNotification
      }
    }
  }
`;

const NOTIFICATION_FRAGMENT = gql`
  ${CONTENT_NOTIFICATION_FRAGMENT}
  ${PROFILE_NOTIFICATION_FRAGMENT}
  ${NOTIFICATION_TARGET_FRAGMENT}
  fragment notification on Notification {
    id
    type
    when
    read
    author {
      ...profile
    }
    target {
      ...target
      ... on GroupInvite {
        id
        inviteDate
        status
        groupTitle
        groupDescriptionSnippet
        groupName
        groupSpaceType
        groupSpaceSlug
        groupSlug
        groupSpacePrivate
        groupAvatar {
          id
          url
          path
        }
      }
      ... on HTMLNotification {
        id
        html
        url
        level
      }
    }
  }
`;

const NOTIFICATIONS_QUERY = gql`
  ${NOTIFICATION_FRAGMENT}
  query Notifications($count: Int, $cursor: String, $sortBy: String) {
    loggedInUser {
      id
      notificationCenter {
        pendingCount
        notifications(first: $count, after: $cursor, sortBy: $sortBy) {
          totalCount
          pageInfo {
            startCursor
            endCursor
            hasNextPage
          }
          edges {
            cursor
            node {
              ...notification
            }
          }
        }
      }
    }
  }
`;

const NOTIFICATIONS_PENDING_COUNT_QUERY = gql`
  query NotificationsPendingCount {
    loggedInUser {
      id
      notificationCenter {
        id
        pendingCount
      }
    }
  }
`;

export const NOTIFICATION_SUBSCRIPTION = gql`
  subscription Notifications {
    notifications {
      id
    }
  }
`;

export class NotificationSubscription extends Subscription {
  static conf = {
    GQL: NOTIFICATION_SUBSCRIPTION,
    resultPath: 'notifications',
  };
}

const MARK_NOTIFICATIONS_READ_MUTATIONS = gql`
  mutation MarkAllAsRead {
    markAllAsRead {
      result
      pendingCount
    }
  }
`;

export class NotificationsQuery extends Query {
  static conf = {
    GQL: NOTIFICATIONS_QUERY,
    varNames: ['cursor', 'sortBy', 'count'],
    resultPath: 'loggedInUser.notificationCenter',
  };
}

export class NotificationsPendingCountQuery extends Query {
  static conf = {
    GQL: NOTIFICATIONS_PENDING_COUNT_QUERY,
    resultPath: 'loggedInUser.notificationCenter',
  };
}

export class MarkAllAsReadMutation extends Mutation {
  static conf = {
    GQL: MARK_NOTIFICATIONS_READ_MUTATIONS,
    resultPath: 'markAllAsRead.pendingCount',
  };
}
